import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Trash2, 
  ShoppingCart, 
  Eye, 
  Filter, 
  Search, 
  ArrowRight,
  Package,
  Briefcase,
  FileText,
  Image,
  Calendar,
  Tag,
  Star,
  Download
} from 'lucide-react';
import { useWishlist } from '../contexts/WishlistContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import WishlistButton from '../components/WishlistButton';
import SharePrintButtons from '../components/SharePrintButtons';

const Wishlist = () => {
  const { items, removeFromWishlist, clearWishlist, getWishlistByType } = useWishlist();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('added');

  // Filter and search items
  const filteredItems = items.filter(item => {
    const matchesFilter = activeFilter === 'all' || item.type === activeFilter;
    const matchesSearch = searchTerm === '' || 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'added':
        return new Date(b.addedAt) - new Date(a.addedAt);
      case 'price':
        return (b.price || 0) - (a.price || 0);
      default:
        return 0;
    }
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'product':
        return Package;
      case 'service':
        return Briefcase;
      case 'project':
        return Image;
      case 'blog':
        return FileText;
      default:
        return Tag;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'product':
        return 'Product';
      case 'service':
        return 'Service';
      case 'project':
        return 'Project';
      case 'blog':
        return 'Blog Post';
      default:
        return 'Item';
    }
  };

  const handleAddToCart = (item) => {
    if (item.type === 'product') {
      addToCart(item);
      navigate('/cart');
    }
  };

  const handleViewItem = (item) => {
    switch (item.type) {
      case 'product':
        navigate(`/product/${item.slug}`);
        break;
      case 'service':
        navigate('/services');
        break;
      case 'project':
        navigate('/portfolio');
        break;
      case 'blog':
        navigate('/blog');
        break;
      default:
        navigate('/');
    }
  };

  const handleRemoveItem = (item) => {
    removeFromWishlist({ id: item.id, type: item.type });
  };

  const getItemStats = (item) => {
    const stats = [];
    
    if (item.rating) {
      stats.push(
        <div key="rating" className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Star className="w-4 h-4 text-yellow-400 fill-current" />
          <span>{item.rating}</span>
        </div>
      );
    }
    
    if (item.downloads) {
      stats.push(
        <div key="downloads" className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Download className="w-4 h-4" />
          <span>{item.downloads}</span>
        </div>
      );
    }
    
    return stats;
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
              <Heart className="w-16 h-16 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Your Wishlist is Empty
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-md mx-auto">
              Start adding items to your wishlist by browsing our products, services, and projects.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/products"
                className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 flex items-center gap-3 shadow-glow"
              >
                <Package className="w-6 h-6" />
                Browse Products
              </Link>
              <Link
                to="/services"
                className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-4 rounded-2xl font-bold hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-all duration-300 flex items-center gap-3 shadow-glow"
              >
                <Briefcase className="w-6 h-6" />
                View Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                My Wishlist
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300">
                {items.length} item{items.length !== 1 ? 's' : ''} in your wishlist
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={clearWishlist}
                className="flex items-center gap-3 px-6 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-all duration-300 font-semibold hover:scale-105"
              >
                <Trash2 className="w-5 h-5" />
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 border-b border-gray-200 dark:border-gray-700">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-8">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search wishlist items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 dark:bg-white/10 backdrop-blur-md text-gray-900 dark:text-white shadow-lg"
              />
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-3">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="px-6 py-4 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg"
              >
                <option value="all" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">All Types</option>
                <option value="product" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Products</option>
                <option value="service" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Services</option>
                <option value="project" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Projects</option>
                <option value="blog" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Blog Posts</option>
              </select>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-6 py-4 border border-gray-300 dark:border-gray-600 rounded-2xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 shadow-lg"
              >
                <option value="added" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Recently Added</option>
                <option value="name" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Name A-Z</option>
                <option value="price" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Price High-Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Items Grid */}
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {sortedItems.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              No items found
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Try adjusting your search or filter criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedItems.map((item) => {
              const TypeIcon = getTypeIcon(item.type);
              
              return (
                <div
                  key={`${item.type}-${item.id}`}
                  className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl hover:scale-105 transition-all duration-500 overflow-hidden border border-white/20"
                >
                  {/* Item Image */}
                  <div className="relative h-56 bg-gradient-to-br from-blue-500 to-purple-600">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TypeIcon className="w-16 h-16 text-white" />
                      </div>
                    )}
                    
                    {/* Type Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold rounded-full shadow-lg">
                        {getTypeLabel(item.type)}
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="absolute top-4 right-4 flex gap-3">
                      <WishlistButton
                        item={item}
                        itemType={item.type}
                        size="small"
                        variant="filled"
                      />
                    </div>
                  </div>

                  {/* Item Content */}
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-2 flex-1">
                        {item.name}
                      </h3>
                    </div>

                    {item.description && (
                      <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-2 mb-6">
                        {item.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-6 mb-6">
                      {getItemStats(item)}
                    </div>

                    {/* Price */}
                    {item.price && (
                      <div className="mb-6">
                        <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {formatPrice(item.price)}
                        </span>
                      </div>
                    )}

                    {/* Added Date */}
                    <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-6">
                      <Calendar className="w-5 h-5" />
                      <span>
                        Added {new Date(item.addedAt).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewItem(item)}
                        className="flex-1 flex items-center justify-center gap-3 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-2xl transition-all duration-300 font-semibold hover:scale-105"
                      >
                        <Eye className="w-5 h-5" />
                        View
                      </button>
                      
                      {item.type === 'product' && (
                        <button
                          onClick={() => handleAddToCart(item)}
                          className="flex-1 flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white rounded-2xl transition-all duration-300 font-semibold shadow-lg"
                        >
                          <ShoppingCart className="w-5 h-5" />
                          Add to Cart
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleRemoveItem(item)}
                        className="px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl transition-all duration-300 hover:scale-105"
                        title="Remove from wishlist"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Share & Print */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <SharePrintButtons
                        item={item}
                        itemType={item.type}
                        size="small"
                        className="justify-center"
                        showLabels={false}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 py-20">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Discover More Amazing Items
          </h2>
          <p className="text-xl text-gray-600 dark:text-white/90 mb-12 max-w-3xl mx-auto">
            Explore our collection of products, services, and projects to find more items for your wishlist.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/products"
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 shadow-glow"
            >
              <Package className="w-6 h-6" />
              Browse Products
              <ArrowRight className="w-6 h-6" />
            </Link>
            <Link
              to="/services"
              className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-3 shadow-glow"
            >
              <Briefcase className="w-6 h-6" />
              View Services
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Wishlist; 